import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import DocumentViewer from '../../components/admin/DocumentViewer';
import ApprovalModal from '../../components/admin/ApprovalModal';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { adminService } from '../../services/admin.service';
import { theme } from '../../config/theme';
import { formatDate } from '../../utils/helpers';
import { ArrowLeftIcon } from '../../components/icons/Icons';

const SellerApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);

  useEffect(() => {
    fetchSellerDetails();
  }, [id]);

  const fetchSellerDetails = async () => {
    try {
      const response = await adminService.getSellerDetails(id);
      setSeller(response.data.seller);
    } catch (error) {
      toast.error('Failed to fetch details');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAction = async (reason) => {
    setActionLoading(true);
    try {
      if (modalAction === 'approve') {
        await adminService.approveSeller(id);
        toast.success('Seller approved successfully!');
      } else {
        await adminService.rejectSeller(id, reason);
        toast.success('Seller rejected');
      }
      setShowModal(false);
      navigate('/admin/sellers/pending');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusVariant = (status) => {
    const map = { pending: 'warning', under_review: 'info', approved: 'success', rejected: 'error' };
    return map[status] || 'default';
  };

  if (loading) return <LoadingSpinner fullScreen />;
  if (!seller) return <div>Seller not found</div>;

  const canApproveOrReject = seller.onboarding_status === 'under_review';

  return (
    <>
      <MainLayout>
        <div style={{ padding: theme.spacing.xl }}>
          <Button 
            variant="ghost" 
            size="md"
            onClick={() => navigate('/admin/sellers/pending')} 
            style={{ marginBottom: theme.spacing.lg }}
          >
            <ArrowLeftIcon size={18} />
            Back
          </Button>

          <Card padding="xl">
            <div style={{ marginBottom: theme.spacing.xl }}>
              <h1 style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.md }}>
                {seller.store_name}
              </h1>
              <Badge variant={getStatusVariant(seller.onboarding_status)}>
                {seller.onboarding_status?.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>

            <div style={{ marginBottom: theme.spacing.xl }}>
              <h3 style={{ marginBottom: theme.spacing.md, fontWeight: theme.typography.fontWeight.semibold }}>Business Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
                <div><div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Store Name</div><div>{seller.store_name}</div></div>
                <div><div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Business Type</div><div>{seller.business_type}</div></div>
                <div><div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Email</div><div>{seller.user?.email}</div></div>
                <div><div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Phone</div><div>{seller.user?.phone} {seller.user?.phone_verified && '✓'}</div></div>
              </div>
            </div>

            <div style={{ marginBottom: theme.spacing.xl }}>
              <h3 style={{ marginBottom: theme.spacing.md, fontWeight: theme.typography.fontWeight.semibold }}>Documents</h3>
              {seller.documents?.map((doc) => <DocumentViewer key={doc.id} document={doc} />)}
            </div>

            {canApproveOrReject && (
              <div style={{ display: 'flex', gap: theme.spacing.md }}>
                <Button
                  variant="danger-outline"
                  size="lg"
                  fullWidth
                  onClick={() => { setModalAction('reject'); setShowModal(true); }}
                >
                  Reject Application
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => { setModalAction('approve'); setShowModal(true); }}
                >
                  Approve Seller
                </Button>
              </div>
            )}
          </Card>
        </div>
      </MainLayout>

      <ApprovalModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        onConfirm={handleConfirmAction} 
        action={modalAction} 
        sellerName={seller.store_name} 
        loading={actionLoading} 
      />
    </>
  );
};

export default SellerApplicationDetail;